import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import A from '@adsk/bim360-matrix-react-a';
import Text from '@adsk/bim360-matrix-react-text';
import breadCrumbArrow from '@adsk/bim360-matrix-react-assets/bread-crumb-arrow.svg';
import './_BreadCrumbs.scss';

/**
 * A breadcrumbs component that gets a list of tabs with name and url/onclick event properties
 * and renders the tabs as breadcrumbs links where the last tab is not clickable.
 */
const BreadCrumbs = props => {
  const classes = classNames('BreadCrumbs', props.className);

  // Last tab is not clickable so we separate it from the others
  const clickableBreadCrumbsArray = props.tabs.slice();
  const lastBreadCrumb = clickableBreadCrumbsArray.pop();

  const _handleOnTabClick = (event, tab) => {
    if (tab.onClick) {
      // ctrl+click or cmd+click should have default behavior of anchor link
      if (event.ctrlKey || event.metaKey) return;

      event.preventDefault();
      tab.onClick();
    }
  };

  const _getTabName = tabName => {
    return typeof tabName === 'function' ? tabName() : tabName;
  };

  const _handleUrl = url => {
    if (typeof url === 'function') {
      return url();
    }

    return url;
  };

  return (
    <div className={classes} style={props.style}>
      {clickableBreadCrumbsArray.map((tab, index) => {
        return (
          <div className="BreadCrumbs__tab-container" key={index}>
            <A
              className="BreadCrumbs__tab"
              onClick={event => _handleOnTabClick(event, tab)}
              href={tab.url && _handleUrl(tab.url)}
              target={(tab.url && tab.target) || '_self'}
            >
              <Text
                containerClassName="BreadCrumbs__tab-container"
                className="BreadCrumbs__tab-name"
                hideTooltip={true}
              >
                {_getTabName(tab.name)}
              </Text>
            </A>
            <SvgIcon className="BreadCrumbs__separator-svg">{breadCrumbArrow}</SvgIcon>
          </div>
        );
      })}
      <div className="BreadCrumbs__tab BreadCrumbs__tab--last">
        <Text className="BreadCrumbs__tab-name" ellipsis={true} hideTooltip={true}>
          {_getTabName(lastBreadCrumb.name)}
        </Text>
      </div>
    </div>
  );
};

BreadCrumbs.propTypes = {
  /**
   * The list of bread crumbs to show
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * A function that returns the name of the tab or the name of the tab as string
       */
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      /**
       * Url to open on mouse middle-click / right-click => open in new tab/window
       * If no onClick is passed for this tab, then also the url to open in a new tab on left click
       */
      url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      /**
       * If Url is provided, the target for clicking on the tab can also be defined. Defaults to `_self`
       */
      target: PropTypes.string,
      /**
       * Optional function to run when the tab is pressed
       */
      onClick: PropTypes.func
    })
  ).isRequired,
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * A custom className for the component container
   */
  className: PropTypes.string
};

export default BreadCrumbs;
